import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("trademind_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("trademind_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("trademind_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me check when returning from Google OAuth — AuthCallback exchanges session_id first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("trademind_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, full_name) => {
    const referral_code = localStorage.getItem("tm_ref_code") || undefined;
    const { data } = await api.post("/auth/register", { email, password, full_name, referral_code });
    localStorage.removeItem("tm_ref_code");
    localStorage.setItem("trademind_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    const referral_code = localStorage.getItem("tm_ref_code") || undefined;
    const { data } = await api.post("/auth/google/session", { session_id: sessionId, referral_code });
    localStorage.removeItem("tm_ref_code");
    localStorage.setItem("trademind_token", data.token);
    setUser(data.user);
    setLoading(false);
    return data.user;
  };

  const logout = () => {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("trademind_token");
    setUser(null);
  };

  const updatePrefs = async (patch) => {
    const { data } = await api.patch("/auth/preferences", patch);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, updatePrefs, loginWithGoogle, processGoogleSession, refreshUser: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
};
